import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import sharedStyles from './AdminShared.module.scss';
import styles from './AdminAnalytics.module.scss';
import AutoRefreshControl from './components/AutoRefreshControl';
import PlaybackPerformancePanel from './components/PlaybackPerformancePanel';
import { useAnalyticsService } from '../../../services/analytics.service';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { isSuperAdmin } from '../../../utils/roles';
import { Routes } from '../../../types/constants';
import {
    AnalyticsPlaybackPerformance,
    AnalyticsPlaybackPerformanceBucket,
    AnalyticsPlaybackPerformanceReport,
    AnalyticsVideoPlaybackPerformance
} from '../../../types/analytics.types';

type VideoAnalyticsTab = 'dashboard' | 'comparison' | 'buckets' | 'videos';

interface MetricDefinition {
    key: keyof Pick<
        AnalyticsPlaybackPerformance,
        'averageStartupMs' | 'averageBufferMs' | 'averageSeekMs' | 'playbackErrorCount' | 'startupSampleCount' | 'bufferEventCount' | 'seekSampleCount'
    >;
    label: string;
    unit: 'ms' | 'count';
    lowerIsBetter: boolean;
}

interface BucketComparison {
    label: string;
    current?: AnalyticsPlaybackPerformanceBucket;
    comparison?: AnalyticsPlaybackPerformanceBucket;
}

const tabs: { id: VideoAnalyticsTab; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'videos', label: 'Videos' },
    { id: 'comparison', label: 'Period Compare' },
    { id: 'buckets', label: 'Quality & Transport' }
];

const slowStartupThresholdMs = 3000;
const highBufferThresholdMs = 3000;

const metricDefinitions: MetricDefinition[] = [
    { key: 'averageStartupMs', label: 'Avg Startup', unit: 'ms', lowerIsBetter: true },
    { key: 'averageBufferMs', label: 'Avg Buffer', unit: 'ms', lowerIsBetter: true },
    { key: 'averageSeekMs', label: 'Avg Seek Recovery', unit: 'ms', lowerIsBetter: true },
    { key: 'playbackErrorCount', label: 'Playback Errors', unit: 'count', lowerIsBetter: true },
    { key: 'startupSampleCount', label: 'Startup Samples', unit: 'count', lowerIsBetter: false },
    { key: 'bufferEventCount', label: 'Buffer Events', unit: 'count', lowerIsBetter: true },
    { key: 'seekSampleCount', label: 'Seek Samples', unit: 'count', lowerIsBetter: false }
];

const toDateInputValue = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const addDays = (date: Date, days: number) => {
    const value = new Date(date);
    value.setDate(value.getDate() + days);
    return value;
};

const defaultToDate = () => toDateInputValue(new Date());
const defaultFromDate = () => toDateInputValue(addDays(new Date(), -29));
const defaultCompareToDate = () => toDateInputValue(addDays(new Date(), -30));
const defaultCompareFromDate = () => toDateInputValue(addDays(new Date(), -59));

const formatDate = (value: string) => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

const formatDateRange = (startUtc: string, endUtc: string) => {
    const end = new Date(endUtc);
    if (!Number.isNaN(end.getTime())) {
        end.setUTCDate(end.getUTCDate() - 1);
    }

    return `${formatDate(startUtc)} - ${Number.isNaN(end.getTime()) ? formatDate(endUtc) : formatDate(end.toISOString())}`;
};

const formatMetricValue = (value: number, unit: MetricDefinition['unit']) => (
    unit === 'ms'
        ? `${Math.round(value).toLocaleString()} ms`
        : Math.round(value).toLocaleString()
);

const formatBytes = (value?: number | null) => {
    if (!Number.isFinite(value ?? Number.NaN)) {
        return '-';
    }

    const bytes = value ?? 0;
    if (bytes < 1024) {
        return `${bytes.toLocaleString()} B`;
    }

    const megabytes = bytes / 1024 / 1024;
    return `${megabytes.toFixed(megabytes >= 100 ? 0 : 1)} MB`;
};

const formatDateTime = (value: string) => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const buildWatchUrl = (row: AnalyticsVideoPlaybackPerformance) => (
    row.videoShortId ? `${Routes.WATCH}?v=${encodeURIComponent(row.videoShortId)}` : null
);

const buildVideoIssueSignals = (row: AnalyticsVideoPlaybackPerformance) => {
    const signals: string[] = [];
    if (row.playbackErrorCount > 0) {
        signals.push('Playback errors');
    }
    if (row.averageStartupMs >= slowStartupThresholdMs) {
        signals.push('Slow start');
    }
    if (row.averageBufferMs >= highBufferThresholdMs) {
        signals.push('High buffer');
    }
    if (!row.fileStatus) {
        signals.push('No DB file');
    } else if (row.fileStatus !== 'Ready') {
        signals.push(`File ${row.fileStatus}`);
    }
    if (row.videoStatus !== 'Ready') {
        signals.push(`Video ${row.videoStatus}`);
    }

    return signals.length > 0 ? signals : ['OK'];
};

const formatDelta = (delta: number, unit: MetricDefinition['unit']) => {
    const rounded = Math.round(delta);
    const prefix = rounded > 0 ? '+' : '';
    return unit === 'ms'
        ? `${prefix}${rounded.toLocaleString()} ms`
        : `${prefix}${rounded.toLocaleString()}`;
};

const buildDeltaClassName = (delta: number, lowerIsBetter: boolean) => {
    if (delta === 0) {
        return styles.deltaNeutral;
    }

    const improved = lowerIsBetter ? delta < 0 : delta > 0;
    return improved ? styles.deltaGood : styles.deltaBad;
};

const buildBucketComparisons = (
    current: AnalyticsPlaybackPerformanceBucket[],
    comparison: AnalyticsPlaybackPerformanceBucket[]
): BucketComparison[] => {
    const labels = new Set<string>();
    current.forEach((bucket) => labels.add(bucket.label));
    comparison.forEach((bucket) => labels.add(bucket.label));

    return Array.from(labels)
        .sort((first, second) => first.localeCompare(second))
        .map((label) => ({
            label,
            current: current.find((bucket) => bucket.label === label),
            comparison: comparison.find((bucket) => bucket.label === label)
        }));
};

/**
 * SuperAdministrator playback diagnostics for comparing player performance before and after delivery changes.
 */
const AdminVideoAnalytics = () => {
    const { getUserInfo } = useUserState();
    const { fetchPlaybackPerformanceReport } = useAnalyticsService();
    const user = getUserInfo();
    const canView = isSuperAdmin(user?.roles);
    const [activeTab, setActiveTab] = useState<VideoAnalyticsTab>('dashboard');
    const [from, setFrom] = useState(defaultFromDate);
    const [to, setTo] = useState(defaultToDate);
    const [compareFrom, setCompareFrom] = useState(defaultCompareFromDate);
    const [compareTo, setCompareTo] = useState(defaultCompareToDate);
    const [includeAdministratorActivity, setIncludeAdministratorActivity] = useState(false);
    const [report, setReport] = useState<AnalyticsPlaybackPerformanceReport | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [autoRefresh, setAutoRefresh] = useState(false);

    const currentRangeLabel = report ? formatDateRange(report.rangeStartUtc, report.rangeEndUtc) : `${from} - ${to}`;
    const comparisonRangeLabel = report
        ? formatDateRange(report.comparisonRangeStartUtc, report.comparisonRangeEndUtc)
        : `${compareFrom} - ${compareTo}`;

    const bucketComparisons = useMemo(() => (
        buildBucketComparisons(report?.current.buckets ?? [], report?.comparison.buckets ?? [])
    ), [report]);

    const loadReport = useCallback(async () => {
        if (!canView) {
            return;
        }

        if (from > to || compareFrom > compareTo) {
            setErrorMessage('The to date must be on or after the from date for both ranges.');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchPlaybackPerformanceReport(
                from,
                to,
                compareFrom,
                compareTo,
                !includeAdministratorActivity
            );
            setReport(response);
        } catch {
            setErrorMessage('Unable to load video playback analytics.');
        } finally {
            setIsLoading(false);
        }
    }, [canView, compareFrom, compareTo, fetchPlaybackPerformanceReport, from, includeAdministratorActivity, to]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    useAutoRefresh(autoRefresh, loadReport);

    if (!canView) {
        return <Navigate to={Routes.ADMIN_ANALYTICS} replace />;
    }

    const renderControls = () => (
        <div className={styles.filters}>
            <label className={styles.filterLabel}>
                Current from
                <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
            </label>
            <label className={styles.filterLabel}>
                Current to
                <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
            </label>
            <label className={styles.filterLabel}>
                Compare from
                <input type="date" value={compareFrom} onChange={(event) => setCompareFrom(event.target.value)} />
            </label>
            <label className={styles.filterLabel}>
                Compare to
                <input type="date" value={compareTo} onChange={(event) => setCompareTo(event.target.value)} />
            </label>
            <label className={styles.toggleLabel}>
                <input
                    type="checkbox"
                    checked={includeAdministratorActivity}
                    onChange={(event) => setIncludeAdministratorActivity(event.target.checked)}
                />
                Include Admin/SuperAdmin activity
            </label>
            <AutoRefreshControl checked={autoRefresh} onChange={setAutoRefresh} />
        </div>
    );

    const renderDashboardTab = () => {
        if (!report) {
            return null;
        }

        return (
            <div className={styles.grid}>
                <PlaybackPerformancePanel
                    title="Current Playback Performance"
                    rangeLabel={currentRangeLabel}
                    performance={report.current}
                />
                <PlaybackPerformancePanel
                    title="Comparison Playback Performance"
                    rangeLabel={comparisonRangeLabel}
                    performance={report.comparison}
                />
            </div>
        );
    };

    const renderComparisonTab = () => {
        if (!report) {
            return null;
        }

        return (
            <section className={sharedStyles.panel}>
                <div className={styles.panelHeading}>
                    <div>
                        <h2>Period Comparison</h2>
                        <p className={styles.rangeLabel}>{currentRangeLabel} compared with {comparisonRangeLabel}</p>
                    </div>
                </div>
                <div className={styles.tableWrap}>
                    <table className={sharedStyles.table}>
                        <thead>
                            <tr>
                                <th>Metric</th>
                                <th>Current</th>
                                <th>Comparison</th>
                                <th>Delta</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metricDefinitions.map((metric) => {
                                const currentValue = report.current[metric.key];
                                const comparisonValue = report.comparison[metric.key];
                                const delta = currentValue - comparisonValue;
                                return (
                                    <tr key={metric.key}>
                                        <td>{metric.label}</td>
                                        <td>{formatMetricValue(currentValue, metric.unit)}</td>
                                        <td>{formatMetricValue(comparisonValue, metric.unit)}</td>
                                        <td className={buildDeltaClassName(delta, metric.lowerIsBetter)}>
                                            {formatDelta(delta, metric.unit)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>
        );
    };

    const renderBucketsTab = () => (
        <section className={sharedStyles.panel}>
            <div className={styles.panelHeading}>
                <div>
                    <h2>Quality & Transport Buckets</h2>
                    <p className={styles.rangeLabel}>{currentRangeLabel} compared with {comparisonRangeLabel}</p>
                </div>
            </div>
            {bucketComparisons.length === 0 ? (
                <p className={sharedStyles.muted}>No quality or transport buckets in these ranges.</p>
            ) : (
                <div className={styles.tableWrap}>
                    <table className={sharedStyles.table}>
                        <thead>
                            <tr>
                                <th>Bucket</th>
                                <th>Current Startup</th>
                                <th>Comparison Startup</th>
                                <th>Current Buffer</th>
                                <th>Comparison Buffer</th>
                                <th>Current Errors</th>
                                <th>Comparison Errors</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bucketComparisons.map((bucket) => (
                                <tr key={bucket.label}>
                                    <td>{bucket.label}</td>
                                    <td>{formatMetricValue(bucket.current?.averageStartupMs ?? 0, 'ms')}</td>
                                    <td>{formatMetricValue(bucket.comparison?.averageStartupMs ?? 0, 'ms')}</td>
                                    <td>{formatMetricValue(bucket.current?.averageBufferMs ?? 0, 'ms')}</td>
                                    <td>{formatMetricValue(bucket.comparison?.averageBufferMs ?? 0, 'ms')}</td>
                                    <td>{formatMetricValue(bucket.current?.playbackErrorCount ?? 0, 'count')}</td>
                                    <td>{formatMetricValue(bucket.comparison?.playbackErrorCount ?? 0, 'count')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );

    const renderVideosTab = () => {
        const rows = report?.current.videos ?? [];

        return (
            <section className={sharedStyles.panel}>
                <div className={styles.panelHeading}>
                    <div>
                        <h2>Videos Played</h2>
                        <p className={styles.rangeLabel}>
                            Current range {currentRangeLabel}. Rows are grouped by video, quality, and transport.
                        </p>
                    </div>
                </div>
                {rows.length === 0 ? (
                    <p className={sharedStyles.muted}>No video playback telemetry in this range.</p>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={sharedStyles.table}>
                            <thead>
                                <tr>
                                    <th>Video</th>
                                    <th>File</th>
                                    <th>Quality / Transport</th>
                                    <th>Startup</th>
                                    <th>Buffer</th>
                                    <th>Seek</th>
                                    <th>Errors</th>
                                    <th>Last Played</th>
                                    <th>Signals</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => {
                                    const watchUrl = buildWatchUrl(row);
                                    const signals = buildVideoIssueSignals(row);
                                    const hasIssue = signals.some((signal) => signal !== 'OK');

                                    return (
                                        <tr key={`${row.videoId}-${row.bucketLabel}-${row.fileFormat}`}>
                                            <td>
                                                {watchUrl ? (
                                                    <a href={watchUrl}>{row.videoTitle}</a>
                                                ) : (
                                                    row.videoTitle
                                                )}
                                                <div className={sharedStyles.muted}>
                                                    {row.videoStatus} / {row.publishStatus}
                                                </div>
                                            </td>
                                            <td>
                                                <strong>{row.fileFormat || '-'}</strong>
                                                <div className={sharedStyles.muted}>
                                                    {row.fileStatus ?? 'No DB file'} · {formatBytes(row.fileSizeBytes)}
                                                </div>
                                                {row.filePath ? <div className={styles.agent}>{row.filePath}</div> : null}
                                            </td>
                                            <td>{row.bucketLabel}</td>
                                            <td>
                                                {formatMetricValue(row.averageStartupMs, 'ms')}
                                                <div className={sharedStyles.muted}>{row.startupSampleCount.toLocaleString()} samples</div>
                                            </td>
                                            <td>
                                                {formatMetricValue(row.averageBufferMs, 'ms')}
                                                <div className={sharedStyles.muted}>{row.bufferEventCount.toLocaleString()} events</div>
                                            </td>
                                            <td>
                                                {formatMetricValue(row.averageSeekMs, 'ms')}
                                                <div className={sharedStyles.muted}>{row.seekSampleCount.toLocaleString()} samples</div>
                                            </td>
                                            <td>{row.playbackErrorCount.toLocaleString()}</td>
                                            <td>{formatDateTime(row.lastPlayedAtUtc)}</td>
                                            <td>
                                                <span
                                                    className={[
                                                        sharedStyles.status,
                                                        hasIssue ? styles.statusFail : styles.statusSuccess
                                                    ].join(' ')}
                                                >
                                                    {signals.join(', ')}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        );
    };

    const renderActiveTab = () => {
        if (isLoading) {
            return <div className={sharedStyles.muted}>Loading video analytics...</div>;
        }

        if (errorMessage) {
            return <div className={sharedStyles.muted}>{errorMessage}</div>;
        }

        switch (activeTab) {
            case 'videos':
                return renderVideosTab();
            case 'comparison':
                return renderComparisonTab();
            case 'buckets':
                return renderBucketsTab();
            default:
                return renderDashboardTab();
        }
    };

    return (
        <div className={sharedStyles.page}>
            <header className={sharedStyles.header}>
                <div>
                    <h1>Video Analytics</h1>
                    <p>Compare playback startup, buffering, seek recovery, and media errors across delivery changes.</p>
                </div>
                {renderControls()}
            </header>

            <div className={styles.tabs} role="tablist" aria-label="Video analytics sections">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={activeTab === tab.id ? styles.activeTab : styles.tab}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {renderActiveTab()}
        </div>
    );
};

export default AdminVideoAnalytics;
